import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, AlertTriangle, Trash2 } from 'lucide-react';
import { errorDev } from '../../utils/logger.js';

const DeleteAccountModal = ({ onConfirm, onCancel, isDeleting = false }) => {
  const { t, ready: i18nReady } = useTranslation(['settings', 'common']);
  const [confirmationText, setConfirmationText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Safe translation function with fallback
  const safeT = (key, fallback = key, namespace = undefined) => {
    if (!i18nReady) return fallback;
    try {
      const result = namespace ? t(key, { ns: namespace }) : t(key);
      return result === key ? fallback : result;
    } catch (err) {
      errorDev("Operation failed:", err);
      return fallback;
    }
  };

  const handleConfirm = async () => {
    if (confirmationText.trim().toUpperCase() !== 'DELETE') {
      alert(safeT('deleteAccountModal.confirmationError', 'Please type "DELETE" to confirm'));
      return;
    }

    setIsProcessing(true);
    try {
      await onConfirm();
    } finally {
      setIsProcessing(false);
    }
  };

  const isConfirmDisabled = confirmationText.trim().toUpperCase() !== 'DELETE' || isProcessing || isDeleting;
  const isLoading = isProcessing || isDeleting;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-neutral-800 rounded-xl max-w-md w-full p-6 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-red-100 dark:bg-red-900/30">
              <AlertTriangle className="text-red-600" size={24} />
            </div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              {safeT('deleteAccountModal.title', 'Delete Account')}
            </h2>
          </div>
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Warning Section */}
        <div className="space-y-4">
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-red-900 dark:text-red-100 text-sm font-medium">
              {safeT('deleteAccountModal.warning', 'This action cannot be undone. Deletion permanently removes your Orrith account and linked records.')}
            </p>
          </div>

          {/* What deletion covers, and what is intentionally retained */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              {safeT('deleteAccountModal.consequencesTitle', 'What happens to your data')}
            </h3>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
              <li className="flex items-start gap-2">
                <span className="text-red-600 mt-1 font-bold">•</span>
                <span>{safeT('deleteAccountModal.consequence1', 'Your Orrith profile, account settings, and personal account metadata.')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-600 mt-1 font-bold">•</span>
                <span>{safeT('deleteAccountModal.consequence2', 'Your products, offers, chats, messages, notifications, ratings, support tickets, and wishlist records.')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-600 mt-1 font-bold">•</span>
                <span>{safeT('deleteAccountModal.consequence3', 'Your social graph links and user-linked impact records that can be safely removed from Orrith data stores.')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-1 font-bold">•</span>
                <span>{safeT('deleteAccountModal.consequence4', 'Kept for integrity: a small number of shared marketplace and support records that mention you (deals, ratings you received, price reports and support history). These stay unlinked from any sign-in, and we show you how many were kept.')}</span>
              </li>
            </ul>
          </div>

          {/* Confirmation Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {safeT('deleteAccountModal.confirmationLabel', 'Type "DELETE" to confirm')}
            </label>
            <input
              type="text"
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              placeholder={safeT('deleteAccountModal.confirmationPlaceholder', 'Type DELETE')}
              disabled={isLoading}
              className="w-full p-3 border border-gray-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={onCancel}
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-neutral-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {safeT('cancel', 'Cancel', 'common')}
            </button>
            <button
              onClick={handleConfirm}
              disabled={isConfirmDisabled}
              className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-500 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium text-sm flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="orrith-loader orrith-loader-xs orrith-loader-on-dark"></div>
                  <span>{safeT('deleting', 'Deleting...', 'common')}</span>
                </>
              ) : (
                <>
                  <Trash2 size={16} />
                  <span>{safeT('deleteAccountModal.confirmButton', 'Delete Account')}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountModal;